<template>
  <div>
    <h4 class="body-1-bold mb-12">
      {{ i18n(translations.shippingFrom) }}
    </h4>
    <RevInputSelect
      id="expeditionCountrySelect"
      v-model="values.expeditionCountry"
      class="mb-12 w-full md:mb-24 md:w-[328px]"
      :error="errors.expeditionCountry"
      :label="i18n(translations.expeditionCountryLabel)"
      :options="expeditionCountryOptions"
    />
    <div class="mb-12 flex items-center space-x-8">
      <h4 class="body-1-bold">
        {{ i18n(translations.vatNumberLabel) }}
      </h4>

      <RevTooltip>
        <template #trigger="{ hide, show }">
          <IconInfo
            variant="secondary"
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>

        {{ i18n(translations.vatInfoTooltip) }}
      </RevTooltip>
    </div>
    <RevInputText
      id="vatNumberInput"
      v-model="values.intraCommunityVatNumber"
      class="w-full md:w-[328px]"
      :disabled="values.isExemptFromVat"
      :error="errors.intraCommunityVatNumber"
      :format="trimValue"
      :label="i18n(translations.vatNumberHint)"
      required
    />
    <span ref="exemptFromVatAnchor"></span>
    <RevCheckbox
      v-if="shouldShowExemptCheckbox"
      id="checkbox"
      :checked="values.isExemptFromVat"
      class="my-16"
      @change="handleChangeExemptFromVat"
    >
      <template #label>
        {{ i18n(translations.exemptedFromVat) }}
      </template>
    </RevCheckbox>
  </div>
  <div v-if="shouldUseVatScheme(values)" data-test="vatSchemeRadios">
    <h4 class="body-1-bold mb-12">
      {{ i18n(translations.vatSchemeRadiosLabel) }}
    </h4>
    <div class="body-2 mb-8">
      {{ i18n(translations.vatSchemeRadiosHint) }}
    </div>
    <RevRadio
      v-for="(label, value) in VAT_MARGIN_SCHEME_VALUES"
      :id="value"
      :key="value"
      v-model="values.vatMarginScheme"
      class="fill-danger mb-16 block"
      name="vatMarginScheme"
      :value
      @click="handleChangeVatMarginScheme"
    >
      <template #label>
        {{ i18n(label) }}
      </template>
    </RevRadio>
    <div
      v-if="errors.vatMarginScheme"
      class="body-2 mt-8 flex items-baseline"
      :class="{ 'text-static-danger-hi': errors.vatMarginScheme }"
    >
      {{ errors.vatMarginScheme }}
    </div>
    <RevInfoBlock
      class="mt-8"
      :content="i18n(translations.marginSchemeInfoBlockContent)"
      :icon="IconInfo"
      :title="i18n(translations.marginSchemeInfoBlockTitle)"
      variant="info"
    />
  </div>
  <div ref="ossRegistrationAnchor" class="my-0"></div>
  <div v-if="shouldUseOss(values)" data-test="ossPickers">
    <h4 class="body-1-bold mb-12">
      {{ i18n(translations.ossRegistrationLabel) }}
    </h4>
    <div class="mb-16 grid grid-cols-2 gap-8 md:flex md:w-[328px] md:gap-0">
      <RevPicker
        class="mr-8 w-[160px] shrink"
        :class="{ 'border-static-danger-hi': errors.isOssRegistered }"
        :label="i18n(translations.yes)"
        :model-value="values.isOssRegistered"
        @update:model-value="handleChangeOssRegistration(true)"
      />
      <RevPicker
        class="w-[160px] shrink"
        :class="{ 'border-static-danger-hi': errors.isOssRegistered }"
        :label="i18n(translations.no)"
        :model-value="isNotOssRegistered(values)"
        @update:model-value="handleChangeOssRegistration(false)"
      />
      <div
        v-if="errors.isOssRegistered"
        class="body-2 mt-8 flex items-baseline"
        :class="{ 'text-static-danger-hi': errors.isOssRegistered }"
      >
        {{ errors.isOssRegistered }}
      </div>
    </div>
    <div v-if="values.isOssRegistered" class="mt-16">
      <p class="my-8">
        {{ i18n(translations.ossDocumentUploadHint) }}
      </p>
      <div class="mt-8 w-[328px]">
        <RevInputFileUpload
          v-if="!values.ossDocuments.length"
          id="oss_document_upload_input"
          v-model="values.files"
          :accept="ALLOWED_FILES_EXTENSIONS"
          data-test="ossDocumentUploadInput"
          :label-empty="i18n(translations.fileInputLabel)"
          :label-not-empty="i18n(translations.fileInputLabel)"
          :max-file-size="ALLOWED_FILES_SIZE"
          :max-files="ALLOWED_FILES_COUNT"
        />
      </div>
      <p
        v-if="errors.files"
        class="body-2 mt-8 flex items-baseline"
        :class="{ 'text-static-danger-hi': errors.files }"
      >
        {{ errors.files }}
      </p>
      <div v-if="values.ossDocuments.length">
        <div
          v-for="document in values.ossDocuments"
          :key="document.id"
          class="body-1 flex w-[328px] items-center justify-between"
        >
          <div class="mr-12 flex truncate">
            {{ document.filename }}
          </div>
          <div class="flex shrink gap-8">
            <RevButtonIcon
              v-if="document.link?.href"
              :aria-label="i18n(translations.downloadButton)"
              :icon="IconDownload"
              target="_blank"
              :to="document.link.href"
              variant="secondary"
            />
            <RevButtonIcon
              :aria-label="'remove' /* TODO missing translation */"
              :icon="IconTrash"
              variant="secondary"
              @click="handleRemoveDocument(values, document)"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isNotOssRegistered(values)"
      class="mt-16 md:mt-56"
      data-test="vatByCountrySubForm"
    >
      <h4 class="body-1-bold mb-12">
        {{ i18n(translations.vatByCountryLabel) }}
      </h4>
      <div class="mb-16 grid grid-cols-1 md:flex">
        <RevInputSelect
          id="marketSelect"
          v-model="countryVatNumberToAdd.market"
          class="mt-8 w-full md:mr-8 md:grow"
          :error="errors.vatNumbersByCountry"
          :label="i18n(translations.country)"
          :options="getMarketOptions(values)"
        />
        <RevInputText
          id="vatNumberByCountryInput"
          v-model="countryVatNumberToAdd.vatNumber"
          class="mt-8 w-full md:mr-8 md:grow"
          :error="errors.vatNumbersByCountry"
          :label="i18n(translations.vatNumber)"
        />
        <RevButton
          class="mt-8"
          :disabled="!isVatByCountryToAddValid"
          :label="i18n(translations.add)"
          variant="primary"
          @click="handleAddVatNumberForCountry(values)"
        >
          {{ i18n(translations.add) }}
        </RevButton>
      </div>
      <RevTable
        v-if="values.vatNumbersByCountry.length"
        :collection="values.vatNumbersByCountry"
        :columns
        transparent-header
      >
        <template #body-country="{ item }">
          <Flag class="mr-8" :country-code="item.market" size="m" />
          <span> {{ i18n.country(item.market) }}</span>
        </template>
        <template #body-vatNumber="{ item }">
          {{ item.vatNumber }}
        </template>
        <template #[`body-${TABLE_COLUMN_ACTION_KEY}`]="{ item }">
          <RevButtonIcon
            :aria-label="'delete' /* TODO missing translation */"
            :data-test="`${item.market}_${item.vatNumber}`"
            :icon="IconTrash"
            variant="secondary"
            @click="handleRemoveVatNumber(values, item)"
          />
        </template>
      </RevTable>
    </div>
    <span ref="bottomAnchor"></span>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, useTemplateRef } from 'vue'

import { type Country, MarketplaceCode } from '@backmarket/http-api'
import type {
  GetLegalDocumentsItem,
  GetVatInformationsVatNumberByCountry,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getAllMarkets } from '@backmarket/nuxt-module-marketplace/getAllMarkets'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCheckbox } from '@ds/components/Checkbox'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { type Option, RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevPicker } from '@ds/components/Picker'
import { RevRadio } from '@ds/components/Radio'
import { RevTable } from '@ds/components/Table'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconTrash } from '@ds/icons/IconTrash'
import type { EventFromInput } from '@ds/types/EventFromInput'
import type { InputValue } from '@ds/types/InputValue'

import { scrollIntoView } from '~/utils/scrollIntoView'

import { FORM_IDS } from '../../../../../../../../Profile.constants'
import {
  ALLOWED_FILES_COUNT,
  ALLOWED_FILES_EXTENSIONS,
  ALLOWED_FILES_SIZE,
} from '../../../../../../LegalDetails.constants'
import { VAT_MARGIN_SCHEME_VALUES } from '../../TaxDetailsModal.constants'
import translations from '../../TaxDetailsModal.translations'
import type { FormTaxKey, FormTaxValues } from '../../TaxDetailsModal.types'
import { shouldUseOss, shouldUseVatScheme } from '../../TaxDetailsModal.utils'

const FORM_ID = FORM_IDS.EDIT_TAX_DETAILS
const TABLE_COLUMN_ACTION_KEY = 'action'

const props = defineProps<{
  initialValues: FormTaxValues
  errors: Partial<Record<FormTaxKey, string>>
  expeditionCountryOptions: Option[]
  shouldShowExemptCheckbox: boolean
}>()

const i18n = useI18n()

const exemptFromVatAnchor = useTemplateRef<HTMLElement>('exemptFromVatAnchor')
const ossRegistrationAnchor = useTemplateRef<HTMLElement>(
  'ossRegistrationAnchor',
)
const bottomAnchor = useTemplateRef<HTMLElement>('bottomAnchor')

const values = computed(() => props.initialValues)

const countryVatNumberToAdd = reactive({
  market: '',
  vatNumber: '',
})

const isVatByCountryToAddValid = computed(
  () => countryVatNumberToAdd.market && countryVatNumberToAdd.vatNumber,
)

const columns = [
  { key: 'country', label: i18n(translations.country) },
  { key: 'vatNumber', label: i18n(translations.vatNumber) },
  { key: TABLE_COLUMN_ACTION_KEY, label: '', align: 'end' as const },
]

// TODO ugly workaround
const APP_COUNTRIES = Object.values(getAllMarkets(MarketplaceCode.EU)).map(
  ({ countryCode }) => countryCode as unknown as Country,
)

const getMarketOptions = ({ vatNumbersByCountry }: FormTaxValues) => {
  const countryCodes = vatNumbersByCountry.map(
    (vatNumber) => vatNumber.market as unknown as Country,
  )

  return APP_COUNTRIES.reduce(
    (markets, market) =>
      countryCodes.includes(market)
        ? markets
        : [
            ...markets,
            {
              label: i18n.country(market),
              value: market,
            },
          ],
    [] as { label: string; value: Country }[],
  )
}

function trimValue(value: InputValue) {
  return typeof value === 'string' ? value.trim() : value
}

function isNotOssRegistered({ isOssRegistered }: FormTaxValues) {
  return isOssRegistered === false
}

function handleRemoveDocument(
  { ossDocuments }: FormTaxValues,
  item: GetLegalDocumentsItem,
) {
  setFormValues(FORM_ID, {
    ossDocuments: ossDocuments.filter((document) => document.id !== item.id),
  })
}

function handleChangeVatMarginScheme() {
  return scrollIntoView(ossRegistrationAnchor)
}

function handleAddVatNumberForCountry({ vatNumbersByCountry }: FormTaxValues) {
  setFormValues(FORM_ID, {
    vatNumbersByCountry: [
      ...vatNumbersByCountry,
      {
        market: countryVatNumberToAdd.market,
        vatNumber: countryVatNumberToAdd.vatNumber,
      },
    ],
  })
  countryVatNumberToAdd.market = ''
  countryVatNumberToAdd.vatNumber = ''
  scrollIntoView(bottomAnchor)
}

function handleChangeExemptFromVat(event: EventFromInput) {
  setFormValues(FORM_ID, {
    intraCommunityVatNumber: '',
    isExemptFromVat: event.target.checked,
  })
  if (!event.target.checked) {
    scrollIntoView(exemptFromVatAnchor)
  }
}

function handleRemoveVatNumber(
  { vatNumbersByCountry }: FormTaxValues,
  item: GetVatInformationsVatNumberByCountry,
) {
  setFormValues(FORM_ID, {
    vatNumbersByCountry: vatNumbersByCountry.filter(
      (vatNumber) => vatNumber.market !== item.market,
    ),
  })
}

function handleChangeOssRegistration(isOssRegistered: boolean) {
  setFormValues(FORM_ID, { isOssRegistered })
  scrollIntoView(ossRegistrationAnchor)
}
</script>
